import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, Select } from '../../components/ui';
import { useListaProveedores, useListaInsumos, useListaTarjetas, useListaEmisores } from '../../hooks/useReferencias';
import { ordenesCargaCombustibleService } from '../../services/ordenesCargaCombustibleService';
import type { OrdenCargaCombustible, OrdenCargaCombustibleEmitirRequest } from '../../types/ordenesCargaCombustible';

interface FormularioEmitirOrdenProps {
  isOpen: boolean;
  orden: OrdenCargaCombustible | null;
  onClose: () => void;
  onSubmit: (datos: OrdenCargaCombustibleEmitirRequest) => void;
  isLoading?: boolean;
}

export const FormularioEmitirOrden: React.FC<FormularioEmitirOrdenProps> = ({
  isOpen,
  orden,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [numeroOrden, setNumeroOrden] = useState('');
  const [proveedorId, setProveedorId] = useState<number | ''>('');
  const [insumoId, setInsumoId] = useState<number | ''>('');
  const [cantidadesPorDestino, setCantidadesPorDestino] = useState<Record<number, string>>({});
  const [unidad, setUnidad] = useState('LITROS');
  const [tarjetaId, setTarjetaId] = useState<number | ''>('');
  const [emisorId, setEmisorId] = useState<number | ''>('');

  const { data: proveedores } = useListaProveedores();
  const { data: insumos } = useListaInsumos();
  const { data: tarjetas } = useListaTarjetas();
  const { data: emisores } = useListaEmisores();

  useEffect(() => {
    if (isOpen && orden) {
      setNumeroOrden('');
      setProveedorId('');
      setInsumoId('');
      setCantidadesPorDestino(
        Object.fromEntries(
          orden.destinos.map((d) => [d.id, d.cantidad_solicitada != null ? String(d.cantidad_solicitada) : ''])
        )
      );
      setUnidad('LITROS');
      setTarjetaId('');
      setEmisorId('');
      // Prefill del correlativo sugerido (editable por el usuario).
      ordenesCargaCombustibleService.siguienteNumero()
        .then((numero) => setNumeroOrden((actual) => actual || numero))
        .catch(() => { /* si falla, el usuario escribe el número a mano */ });
    }
  }, [isOpen, orden]);

  const handleClose = () => {
    onClose();
  };

  const esValido = !!proveedorId && !!insumoId && numeroOrden.trim() !== '' &&
    !!orden && orden.destinos.every((d) => Number(cantidadesPorDestino[d.id]) > 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!esValido || !orden) return;
    onSubmit({
      numero_orden: numeroOrden.trim(),
      proveedor_id: Number(proveedorId),
      insumo_id: Number(insumoId),
      autorizaciones: orden.destinos.map((d) => ({
        destino_id: d.id,
        cantidad_autorizada: Number(cantidadesPorDestino[d.id]),
      })),
      unidad,
      tarjeta_id: tarjetaId ? Number(tarjetaId) : undefined,
      emisor_id: emisorId ? Number(emisorId) : undefined,
    });
  };

  if (!orden) return null;

  const textoDestino = (d: OrdenCargaCombustible['destinos'][number]) => d.vehiculo
    ? `${t('ordenes_carga.table.vehiculo')}: ${d.vehiculo.codigo} - ${d.vehiculo.patente}`
    : d.maquina
      ? `${t('ordenes_carga.table.maquina')}: ${d.maquina.codigo} - ${d.maquina.descripcion || ''}`
      : '';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('ordenes_carga.emitir.title')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700 space-y-1">
          <div>{t('ordenes_carga.table.usuario_autorizado')}: {orden.operador?.codigo} - {orden.operador?.descripcion}</div>
          {orden.observaciones_solicitud && (
            <div>{t('ordenes_carga.solicitar.observaciones')}: {orden.observaciones_solicitud}</div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('ordenes_carga.form.numero_orden')}
            value={numeroOrden}
            onChange={(e) => setNumeroOrden(e.target.value)}
            required
          />
          <Select
            label={t('ordenes_carga.form.proveedor')}
            value={proveedorId}
            onChange={(e) => setProveedorId(e.target.value ? Number(e.target.value) : '')}
            options={[
              { value: '', label: t('common.select') },
              ...(proveedores || []).map(p => ({ value: p.id, label: `${p.codigo} - ${p.nombre}` })),
            ]}
            required
          />
          <Select
            label={t('ordenes_carga.form.insumo')}
            value={insumoId}
            onChange={(e) => setInsumoId(e.target.value ? Number(e.target.value) : '')}
            options={[
              { value: '', label: t('common.select') },
              ...(insumos || []).map(i => ({ value: i.id, label: `${i.codigo} - ${i.descripcion}` })),
            ]}
            required
          />
          <Input
            label={t('ordenes_carga.form.unidad')}
            value={unidad}
            onChange={(e) => setUnidad(e.target.value)}
          />
          <Select
            label={t('ordenes_carga.form.tarjeta')}
            value={tarjetaId}
            onChange={(e) => setTarjetaId(e.target.value ? Number(e.target.value) : '')}
            options={[
              { value: '', label: t('common.select') },
              ...(tarjetas || []).map(tj => ({
                value: tj.id,
                label: tj.compania ? `${tj.compania} - ${tj.numero}` : tj.numero,
              })),
            ]}
          />
          <Select
            label={t('ordenes_carga.form.emisor')}
            value={emisorId}
            onChange={(e) => setEmisorId(e.target.value ? Number(e.target.value) : '')}
            options={[
              { value: '', label: t('common.select') },
              ...(emisores || []).map(em => ({
                value: em.id,
                label: em.cargo ? `${em.nombre} (${em.cargo})` : em.nombre,
              })),
            ]}
          />
        </div>

        <div className="space-y-3">
          {orden.destinos.map((d) => (
            <div key={d.id} className="border border-gray-200 rounded-md p-3 flex items-center justify-between gap-4">
              <span className="text-sm text-gray-700">{textoDestino(d)}</span>
              <div className="w-40">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  label={t('ordenes_carga.form.cantidad_autorizada')}
                  value={cantidadesPorDestino[d.id] ?? ''}
                  onChange={(e) => setCantidadesPorDestino((prev) => ({ ...prev, [d.id]: e.target.value }))}
                  required
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!esValido} loading={isLoading}>
            {t('ordenes_carga.emitir.confirmar')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FormularioEmitirOrden;
